import { useEffect } from "react";
import { Button } from "antd";
import ReloadOutlined from "@ant-design/icons/ReloadOutlined";
import { LOCAL_STORAGE } from "@constants";
import { ErrorScreen } from "@components/ErrorScreen";

const FORCED_RELOAD_TIMEOUT = 1000 * 60 * 5;
const FORCED_RELOAD_STORAGE = LOCAL_STORAGE.FORCED_RELOAD_STORAGE;

const CHUNK_ERROR_MESSAGES = [
  "error loading dynamically imported module", // chromium based
  "failed to fetch dynamically imported module", // firefox, safari
];

const isChunkErrorMessage = (errorMsg: string) =>
  !!errorMsg &&
  CHUNK_ERROR_MESSAGES.some((chunkMsg) =>
    errorMsg.toLowerCase().includes(chunkMsg),
  );

const shouldForceReload = () => {
  const storedLastReload = localStorage.getItem(FORCED_RELOAD_STORAGE);
  const lastReload = parseInt(storedLastReload || "") || 0;
  const timeSinceLastReload = Date.now() - lastReload;
  return timeSinceLastReload > FORCED_RELOAD_TIMEOUT;
};

const reload = () => window.location.reload();

const forceReload = () => {
  localStorage.setItem(FORCED_RELOAD_STORAGE, Date.now().toString());
  reload();
};

export const useReloadUpdatedApp = (errorMsg: string) => {
  const isChunkError = isChunkErrorMessage(errorMsg);
  const shouldAutoReload = isChunkError && shouldForceReload();

  useEffect(() => {
    if (shouldAutoReload) forceReload();
  }, [shouldAutoReload]);

  return (
    isChunkError && <ReloadUpdatedApp shouldAutoReload={shouldAutoReload} />
  );
};

type ReloadUpdatedAppProps = {
  shouldAutoReload: boolean;
};

const ReloadUpdatedApp = ({ shouldAutoReload }: ReloadUpdatedAppProps) => (
  <ErrorScreen
    title="Uma nova versão da aplicação está disponível!"
    subtitle={
      shouldAutoReload
        ? "A página será atualizada automaticamente em alguns instantes."
        : "Clique no botão abaixo para atualizar a página e acessar a última versão da aplicação."
    }
  >
    <Button type="primary" icon={<ReloadOutlined />} onClick={reload}>
      Atualizar página
    </Button>
  </ErrorScreen>
);
